import {css, html} from 'lit';
import {customElement, property, query, state} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import {styleMap} from 'lit-html/directives/style-map.js';
import {SpBaseInput} from '../sp-base-input';
import {SpDialog} from '../../sp-dialog/sp-dialog';
import {FlatpickrValueChanged} from '../../sp-flatpickr/sp-flatpickr';
import {when} from 'lit/directives/when.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import {key as LocaleKey} from 'flatpickr/dist/types/locale';
import {unsafeCSSIcon} from "../../../styles/icons";

@customElement('sp-datetime-input')
export class SpDatetimeInput extends SpBaseInput<Date> {
    @state() format: string = 'LLL';
    @state() bgClass: string;
    @state() statusClass: string;

    @property({ type: Date }) value: Date;
    @property({ type: String }) dialogHeaderText;
    @property({ type: String }) dialogOkText;
    @property({ type: String }) dialogCancelText;
    @property({ type: Boolean, reflect: true }) disabled;
    @property({ type: String }) locale: LocaleKey;
    @property({ type: String }) placeholder;
    @property({ type: Function }) valueFormatter: (date: Date) => string;

    datePickerValue: Date;

    @query('sp-default-dialog') dialog: SpDialog;

    static get styles() {
        return [
            //language=CSS
            css`
                .label {
                    white-space: nowrap;
                    display: block;
                    position: relative;
                    width: 100%;
                    height: 45px;
                    box-sizing: border-box;
                    box-shadow: var(--sp-default-box-shadow);
                    background-image: url(${unsafeCSSIcon('calendar', { size: 'x128' })});
                    background-repeat: no-repeat;
                    background-position: right 23px center;
                    background-size: 24px auto;
                    border-radius: 3px;
                    overflow: hidden;
                    cursor: pointer;
                }

                .label div {
                    display: block;
                    position: absolute;
                    left: 0;
                    right: 70px;
                    background-color: #fff;
                    height: 45px;
                    box-sizing: border-box;
                    font-size: 14px;
                    line-height: 45px;
                    padding: 0 10px;
                }

                .placeholder {
                    color: #aaa;
                }

                .active {
                    color: #000;
                }

                .disabled {
                    color: #aaa;
                }

                .col {
                    padding-left: 5px;
                    margin-top: 5px;
                    box-sizing: border-box;
                }

                .clear {
                    clear: both;
                }
            `
        ];
    }

    getDialogTemplate() {
        return html`
            <sp-default-dialog
                    .template=${html`
                        <sp-flatpickr
                                .locale=${ifDefined(this.locale)}
                                .value=${this.value}
                                @valueChanged=${this.valueChanged.bind(this)}
                        ></sp-flatpickr>`}
                    .headerText=${this.dialogHeaderText}
                    .buttons="${
                            [
                                {
                                    text: this.dialogOkText,
                                    textPosition: 'center',
                                    clickCallback: () => {
                                        this.save();
                                    }
                                },
                                {
                                    text: this.dialogCancelText,
                                    textPosition: 'center',
                                    clickCallback: () => {
                                        this.dialog.hide();
                                    }
                                }
                            ]}">
            </sp-default-dialog>`;
    }

    openDialog() {
        if (!this.disabled) {
            this.dialog.show();
        }
    }

    valueChanged(event: FlatpickrValueChanged) {
        this.datePickerValue = event.detail.value;
    }

    async save() {
        this.dialog.hide();
        this.value = this.datePickerValue;
        this.emit('change');
    }

    printValue() {
        if (this.valueFormatter) {
            return this.valueFormatter(this.value);
        } else {
            return this.value;
        }
    }

    protected render() {
        return html`
            ${this.getDialogTemplate()}
            <div
                    class="${classMap({
                        label: true,
                        active: this.value instanceof Date && !this.disabled,
                        placeholder: this.value instanceof Date && this.disabled,
                        disabled: this.disabled
                    })}"
                    style="${styleMap({
                        'background-color': this.disabled ? 'rgb(var(--sp-color-d))' : 'rgb(var(--sp-color-b))'
                    })}"
                    @click="${this.openDialog}">
                <div>${when(this.value, () => this.printValue(), () => this.placeholder)}</div>
            </div>
        `;
    }
}
